import Vue from "vue";
import Component from "vue-class-component";

/**
 * Mixin that fixes Vuetify 2's v-expansion-panel ARIA violation.
 *
 * Vuetify places aria-expanded on a plain <div>, which is invalid.
 * This mixin uses a MutationObserver to move aria-expanded onto the
 * inner <button> (v-expansion-panel-header) for every panel in the
 * component, keeping it in sync whenever Vuetify updates the value.
 *
 * Usage:
 *   @Component({ mixins: [ExpansionPanelAriaMixin] })
 *   export default class MyComponent extends Vue { ... }
 */
@Component
export default class ExpansionPanelAriaMixin extends Vue {
  private _expansionPanelObservers: MutationObserver[] = [];

  mounted() {
    this.$nextTick(() => {
      this._setupExpansionPanelAria();
    });
  }

  beforeDestroy() {
    this._expansionPanelObservers?.forEach(obs => obs.disconnect());
    this._expansionPanelObservers = [];
  }

  private _setupExpansionPanelAria() {
    const panels = this.$el.querySelectorAll<HTMLElement>(".v-expansion-panel");

    panels.forEach(panel => {
      const button = panel.querySelector<HTMLElement>(
        ".v-expansion-panel-header"
      );
      if (!button) return;

      const sync = () => {
        const expanded = panel.getAttribute("aria-expanded");
        if (expanded !== null) {
          button.setAttribute("aria-expanded", expanded);
          panel.removeAttribute("aria-expanded");
        }
      };

      sync();

      const observer = new MutationObserver(sync);
      observer.observe(panel, {
        attributes: true,
        attributeFilter: ["aria-expanded"]
      });
      this._expansionPanelObservers?.push(observer);
    });
  }
}
